import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import AuthService from '../services/authService';
import { useHasMultipleWorkspaces } from '../utils/workspaces';
import axiosInstance from '../utils/axiosInstance';
import { getImageURL } from '../utils/api';
import TeamworkButton from './TeamworkButton';
import NotificationService from '../services/notificationService';
import TeamworkLiveToast from './TeamworkLiveToast';
import { prepareBrowserNotifications, showTeamworkBrowserNotification } from '../utils/browserNotifications';
import { subscribePush } from '../utils/webPushClient';

const Header = styled.header`
  position: sticky;
  top: 0;
  z-index: 30;
  display: flex;
  height: 64px;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid #e2e8f0;
  background: #ffffff;
  padding: 0 24px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04);
`;

const Left = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const Right = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const IconButton = styled.button`
  border: none;
  background: transparent;
  color: #475569;
  padding: 8px;
  border-radius: 8px;
  cursor: pointer;
  &:hover { background: #f1f5f9; color: #0f172a; }
`;

const Input = styled.input`
  width: 256px;
  border: 1px solid #cbd5e1;
  background: #f8fafc;
  border-radius: 8px;
  padding: 8px 12px 8px 36px;
  font-size: 14px;
  outline: none;
  &:focus { border-color: #10b981; box-shadow: 0 0 0 2px rgba(16,185,129,0.2); }
`;

const Dropdown = styled.div`
  position: absolute;
  right: 0;
  margin-top: 8px;
  width: 224px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -4px rgba(0,0,0,0.1);
`;

const Row = styled.div`
  padding: 10px 16px;
  border-bottom: 1px solid #e2e8f0;
`;

const MenuBtn = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border: none;
  background: transparent;
  cursor: pointer;
  font-size: 14px;
  color: #334155;
  &:hover { background: #f1f5f9; }
`;

const RoleBadge = styled.span`
  display: inline-block;
  border-radius: 6px;
  padding: 4px 10px;
  font-size: 12px;
  font-weight: 600;
  border: 1px solid #cbd5e1;
`;

export default function AdminSalesTopBar({ title, onMenuClick }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [showUserMenu, setShowUserMenu] = useState(false);
  const hasMultipleWorkspaces = useHasMultipleWorkspaces();
  const [teamworkCount, setTeamworkCount] = useState(0);
  const [teamworkOverdue, setTeamworkOverdue] = useState(false);
  const stored = AuthService.getUser();
  const teamworkSocketUserId = stored?.superAdmin?.superAdminId || stored?.superAdmin?.staff?.staffId;
  const teamworkSocketUserType = stored?.superAdmin?.superAdminId ? 'super_admin' : 'staff';
  const currentStaffId = stored?.superAdmin?.staff?.staffId || null;
  const currentSuperAdminId = stored?.superAdmin?.superAdminId || null;

  const loadTeamworkCount = useCallback(async () => {
    try {
      const res = await axiosInstance.get('/admin/staff-teamwork/count').catch(() => null);
      if (res?.data?.count !== undefined) {
        setTeamworkCount(res.data.count);
        setTeamworkOverdue((res.data.overdueCount || 0) > 0);
      }
    } catch {}
  }, []);

  useEffect(() => {
    loadTeamworkCount();
    const interval = setInterval(loadTeamworkCount, 60000);
    return () => clearInterval(interval);
  }, [loadTeamworkCount]);

  useEffect(() => {
    window.addEventListener('ka-teamwork-count-changed', loadTeamworkCount);
    const storageHandler = (e) => { if (e.key === 'ka_tw_refresh') loadTeamworkCount(); };
    window.addEventListener('storage', storageHandler);
    return () => {
      window.removeEventListener('ka-teamwork-count-changed', loadTeamworkCount);
      window.removeEventListener('storage', storageHandler);
    };
  }, [loadTeamworkCount]);

  useEffect(() => {
    if (!teamworkSocketUserId) return;
    NotificationService.connect(
      teamworkSocketUserId,
      teamworkSocketUserType,
      stored?.superAdmin?.name || stored?.superAdmin?.staff?.name || '',
      stored?.superAdmin?.staff?.staffId || null
    );
    prepareBrowserNotifications();
    subscribePush(teamworkSocketUserId, 'admin');
    const offCount = NotificationService.onTeamworkCountUpdated(loadTeamworkCount);
    const offMessage = NotificationService.onTeamworkMessageAlert((payload = {}) => {
      loadTeamworkCount();

      // Sender is excluded server-side, but keep a local safety check.
      const isSelfSender =
        (payload?.senderType === 'staff' && Number(payload?.senderStaffId) === Number(currentStaffId)) ||
        (payload?.senderType === 'super_admin' && Number(payload?.senderSuperAdminId) === Number(currentSuperAdminId));
      if (isSelfSender) return;

      const title = payload?.title || (payload?.teamworkCode
        ? `${payload.teamworkCode} - New Teamwork Message`
        : 'New Teamwork Message');
      const body = payload?.body || payload?.messagePreview || 'You received a new teamwork message.';
      window.dispatchEvent(new CustomEvent('ka-teamwork-live-alert', {
        detail: {
          title,
          body,
          link: payload?.teamworkId ? `/admin/teamwork?teamworkId=${encodeURIComponent(payload.teamworkId)}` : '/admin/teamwork',
        },
      }));

      showTeamworkBrowserNotification(payload);
    });
    return () => {
      offCount();
      offMessage();
      NotificationService.disconnect();
    };
  }, [teamworkSocketUserId, teamworkSocketUserType, loadTeamworkCount, currentStaffId, currentSuperAdminId]);

  const getUserInfo = () => {
    if (!stored || !stored.superAdmin) {
      return { name: 'Guest User', email: 'guest@example.com', role: 'Guest', avatar: undefined };
    }
    const sa = stored.superAdmin;
    const staff = sa.staff;
    if (staff) {
      return {
        name: staff.name || 'Admin User',
        email: staff.officeEmail || staff.gmail || '',
        role: 'Sales Admin',
        avatar: staff.imagePath || undefined,
      };
    }
    return { name: 'User', email: '', role: 'Sales Admin', avatar: undefined };
  };

  const user = getUserInfo();

  const handleSearch = (e) => {
    e.preventDefault();
    console.log('Searching for:', searchQuery);
  };

  const handleLogout = async () => {
    try {
      await axiosInstance.post('/logout');
    } catch {}
    AuthService.clear();
    window.location.href = '/';
  };

  const roleColors = {
    'Sales Admin': { bg: 'linear-gradient(90deg,#d1fae5,#a7f3d0)', color: '#065f46', border: '#6ee7b7' },
  };
  const rc = roleColors[user.role] || { bg: 'linear-gradient(90deg,#f3f4f6,#e2e8f0)', color: '#1f2937', border: '#cbd5e1' };

  return (
    <Header>
      <Left>
        <IconButton onClick={onMenuClick} aria-label="Toggle menu">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16"/></svg>
        </IconButton>
        {title && <h1 style={{ fontSize: 18, fontWeight: 600, color: '#111827' }}>{title}</h1>}
      </Left>
      <Right>
        <TeamworkLiveToast />
        {/* Teamwork Icon Button */}
        <TeamworkButton count={teamworkCount} overdue={teamworkOverdue} onClick={() => window.open('/admin/teamwork', '_blank')} />
        <form onSubmit={handleSearch} style={{ display: 'flex', alignItems: 'center' }}>
          <div style={{ position: 'relative' }}>
            <Input type="text" placeholder="Search..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
            <svg style={{ position: 'absolute', left: 12, top: 10 }} width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#9ca3af"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/></svg>
          </div>
        </form>
        <div style={{ position: 'relative' }}>
          <IconButton onClick={() => setShowUserMenu((v) => !v)} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 12px' }}>
            {user.avatar && (
              <img
                src={getImageURL(user.avatar)}
                alt={user.name || 'User'}
                style={{ height: 32, width: 32, borderRadius: '9999px', objectFit: 'cover' }}
              />
            )}
            <span style={{ marginLeft: 8, fontSize: 14, color: '#374151', fontWeight: 500 }}>{user.name || 'User'}</span>
            <svg style={{ marginLeft: 4 }} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#9ca3af"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"/></svg>
          </IconButton>
          {showUserMenu && (
            <Dropdown>
              <Row>
                <p style={{ fontSize: 14, fontWeight: 600, color: '#111827' }}>{user.name}</p>
                <p style={{ marginTop: 4, fontSize: 12, color: '#6b7280' }}>{user.email}</p>
                <RoleBadge style={{ marginTop: 8, background: rc.bg, color: rc.color, borderColor: rc.border }}>{user.role}</RoleBadge>
              </Row>
              <div>
                <MenuBtn onClick={() => (window.location.href = '/admin/profile')}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/></svg>
                  Your Profile
                </MenuBtn>
                {hasMultipleWorkspaces && (
                  <MenuBtn onClick={() => (window.location.href = '/workspaces')}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7h12m0 0l-4-4m4 4l-4 4M16 17H4m0 0l4-4m-4 4l4 4"/></svg>
                    Switch Workspace
                  </MenuBtn>
                )}
              </div>
              <div style={{ borderTop: '1px solid #e5e7eb' }}>
                <MenuBtn onClick={handleLogout} style={{ color: '#dc2626' }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"/></svg>
                  Sign Out
                </MenuBtn>
              </div>
            </Dropdown>
          )}
        </div>
      </Right>
    </Header>
  );
}
